import { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Switch,
  Button,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePlatformLeadAccess } from '../home/usePlatformLeadAccess';

const useStyles = makeStyles(theme => ({
  card: {
    marginTop: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  refreshButton: {
    marginTop: theme.spacing(1),
  },
}));

/**
 * Settings card for toggling the Agent Registry entity provider at runtime.
 * Only visible to platform-lead group members.
 */
export const AgentRegistryProviderCard = () => {
  const classes = useStyles();
  const isPlatformLead = usePlatformLeadAccess();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getBaseUrl = useCallback(async () => {
    const appBase = await discoveryApi.getBaseUrl('app');
    // Strip /api/app to get the backend root
    return appBase.replace(/\/api\/app$/, '');
  }, [discoveryApi]);

  const fetchStatus = useCallback(async () => {
    try {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(
        `${base}/api/agent-registry-provider/status`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEnabled(data.enabled);
      setError(null);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, [fetchApi, getBaseUrl]);

  useEffect(() => {
    if (isPlatformLead) {
      fetchStatus();
    }
  }, [isPlatformLead, fetchStatus]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(
        `${base}/api/agent-registry-provider/toggle`,
        { method: 'POST' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEnabled(data.enabled);
      setError(null);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setToggling(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const base = await getBaseUrl();
      const res = await fetchApi.fetch(
        `${base}/api/agent-registry-provider/refresh`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setError(null);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setRefreshing(false);
    }
  };

  if (!isPlatformLead) {
    return null;
  }

  return (
    <Card className={classes.card}>
      <CardHeader title="Agent Registry Provider" />
      <CardContent>
        <Typography className={classes.description}>
          Auto-discovers agents from the Agent Gateway registry and creates
          catalog entries. Changes take effect immediately without restart.
        </Typography>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <div className={classes.row}>
              <Typography>
                {enabled ? 'Enabled' : 'Disabled'}
              </Typography>
              <Switch
                checked={enabled ?? false}
                onChange={handleToggle}
                disabled={toggling}
                color="primary"
              />
            </div>
            {enabled && (
              <Button
                className={classes.refreshButton}
                variant="outlined"
                size="small"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing…' : 'Refresh Now'}
              </Button>
            )}
            {error && (
              <Typography color="error" variant="caption">
                {error}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
